import * as angular from 'angular';
import './styling.scss';
import * as moment from 'moment';

import { CurrentUserService } from 'services/currentUser';
import { getFullName } from 'common/source/utilities/policyInfoHelper';
import { getPolicyRelations, isValidPendingRenewalPolicy } from 'utilities/policyInfosHelper';
import { getMomentFromHippoDateString } from 'common/source/utilities/dates';
import { TransitionService } from '@uirouter/angularjs';

const module = angular.module('intercom', []);

interface IntercomController extends angular.IStrictController {
    intercomContainer: HTMLElement | null;
    intercomState: IntercomState;
}

enum IntercomState {
    BootedLoggedIn = 'BootedLoggedIn',
    BootedLoggedOut = 'BootedLoggedOut',
    Off = 'Off'
}
enum IntercomAction {
    Boot = 'boot',
    Shutdown = 'shutdown',
    Login = 'login',
    Logout = 'logout'
}

module.component('intercom', {
    controller: function(
        this: IntercomController,
        currentUserService: CurrentUserService,
        $q: angular.IQService,
        $transitions: TransitionService
    ) {
        'ngInject';

        // Returns the number of days until the closest policy renewal date if the user has any policies
        // pending renewal, otherwise returns undefined
        const getDaysUntilNearestRenewalDate = (policyInfos: Array<CustomerPolicyInfo>) => {
            const policyRelations = getPolicyRelations(policyInfos);
            const daysUntilRenewal = policyInfos.reduce((acc: number | undefined, policyInfo: CustomerPolicyInfo) => {
                const policyRelation = policyRelations[policyInfo.id];
                const isPendingActiveRenewal = isValidPendingRenewalPolicy(policyInfo, policyRelation);
                if (isPendingActiveRenewal) {
                    const renewalDate = getMomentFromHippoDateString(policyInfo.effective_date);
                    const daysFromNow = renewalDate.diff(moment(), 'days');
                    if (acc === undefined) {
                        return daysFromNow;
                    }
                    return acc < daysFromNow ? acc : daysFromNow;
                }
                return acc;
            }, undefined);
            return daysUntilRenewal;
        };

        // Insert custom css into intercom's iframe since they don't support custom launcher icons.
        const injectCustomCSS = () => {
            const stylesheet = document.createElement('link');
            stylesheet.rel = 'stylesheet';
            stylesheet.href = '/styling.css';

            // Hide intercom container before injecting custom css to avoid
            // flickering of different styles that we apply.
            toggleIntercomDisplay(false);

            const interval = setInterval(() => {
                const iframe = document.querySelector('.intercom-launcher-frame') as HTMLIFrameElement;

                if (iframe && iframe.contentDocument) {
                    iframe.contentDocument.head.appendChild(stylesheet);
                    toggleIntercomDisplay(true);
                    clearInterval(interval);
                }
            }, 100);
        };

        const toggleIntercomDisplay = (showIntercom: boolean) => {
            const intercomContainer = document.getElementById('intercom-container');
            if (intercomContainer) {
                intercomContainer.style.display = showIntercom ? 'block' : 'none';
            }
        };

        // Abstract the request to fetch the Identity Verification hash so we can swallow potential errors.
        // This allows us to use Promise.all() in initializeLoggedInIntercom, and still boot Intercom without Identity Verification
        const fetchIdentityVerificationHash = () => {
            return currentUserService
                .getIntercomIdentityVerificationHash()
                .then((res) => res.Customer)
                .catch(() => null);
        };

        // Boot Intercom with Identity Verification and identifiable info based on the policies received.
        // If Identity Verification hash is not available, we still want to boot Intercom without Identity Verification
        const initializeLoggedInIntercom = (mode: Intercom_.IntercomCommand = 'boot') => {
            $q.all([currentUserService.getDisplayOrderedCustomerPolicyInfos(), fetchIdentityVerificationHash()]).then(
                ([policyInfos, intercomVerification]) => {
                    let intercomConfig: Partial<Intercom_.IntercomSettings> = {};
                    let policyInfo;
                    if (policyInfos && policyInfos.length) {
                        policyInfo = policyInfos[0];
                        getDaysUntilNearestRenewalDate(policyInfos);

                        intercomConfig = {
                            name: getFullName(policyInfo),
                            days_until_nearest_renewal: getDaysUntilNearestRenewalDate(policyInfos)
                        };
                    }
                    // Sending email, user_id, OR user_hash will tell Intercom to use Identity Verification. If only one of these
                    // is missing and another one present, Intercom will fail to boot.
                    if (intercomVerification && policyInfo) {
                        intercomConfig = {
                            ...intercomConfig,
                            email: policyInfo.personal_information.email,
                            user_id: intercomVerification.id,
                            user_hash: intercomVerification.intercomUserHash
                        };
                    }
                    initializeIntercom(mode, intercomConfig);
                }
            );
        };

        const initializeIntercom = (
            mode: Intercom_.IntercomCommand = 'boot',
            options: Partial<Intercom_.IntercomSettings> = {}
        ) => {
            const intercomSettings = {
                app_id: window.appConfig.CUSTOMER_INTERCOM_KEY,
                ...options
            };
            Intercom(mode, intercomSettings);
            injectCustomCSS();
        };

        const intercomReducer = (
            prevState: IntercomState = IntercomState.Off,
            action?: IntercomAction
        ): IntercomState => {
            switch (action) {
                case IntercomAction.Shutdown:
                    if (prevState !== IntercomState.Off) {
                        initializeIntercom('shutdown');
                    }
                    return IntercomState.Off;
                case IntercomAction.Login:
                    if (prevState === IntercomState.Off) {
                        initializeLoggedInIntercom(); // boot logged-in
                    } else if (prevState === IntercomState.BootedLoggedOut) {
                        // shutdown, reboot logged-in
                        initializeIntercom('shutdown');
                        initializeLoggedInIntercom();
                    }
                    // Do nothing if intercom is booted and user is already logged in. Handles transitions between logged-in pages
                    return IntercomState.BootedLoggedIn;
                case IntercomAction.Logout:
                    if (prevState === IntercomState.Off) {
                        initializeIntercom(); // boot logged-out
                    } else if (prevState === IntercomState.BootedLoggedIn) {
                        initializeIntercom('shutdown');
                        return IntercomState.Off;
                    }
                    return IntercomState.BootedLoggedOut;
            }
            return prevState;
        };

        this.$onInit = () => {
            if (window.appConfig && !window.appConfig.CUSTOMER_INTERCOM_KEY) {
                return;
            }
            this.intercomState = intercomReducer();

            // Relies on angular router transitions.
            $transitions.onSuccess({}, () => {
                const actionType = currentUserService.isLoggedIn() ? IntercomAction.Login : IntercomAction.Logout;
                this.intercomState = intercomReducer(this.intercomState, actionType);
            });
        };
    }
});

export { module };
